import { Paperclip, Upload, X } from "lucide-react";
import { api, type CertificationEntityType } from "./api";
import {
  ATTACHMENT_ACCEPT,
  attachmentSelectionError,
} from "./attachment-policy";

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
  onError: (message: string) => void;
  label?: string;
};

const fileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} MB`
    : `${Math.max(1, Math.ceil(bytes / 1024)).toLocaleString("pt-BR")} KB`;

export const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
    reader.readAsDataURL(file);
  });

export async function uploadCertificationAttachments(
  entityType: CertificationEntityType,
  entityId: string,
  files: File[],
) {
  for (const file of files) {
    await api.addCertificationEntityAttachment(entityType, entityId, {
      fileName: file.name,
      mimeType: file.type || "application/octet-stream",
      size: file.size,
      contentBase64: await fileToBase64(file),
    });
  }
}

export function CertificationAttachmentPicker({
  files,
  onChange,
  onError,
  label = "Anexos",
}: Props) {
  const addFiles = (selected: File[]) => {
    const combined = [...files, ...selected].filter(
      (file, index, list) =>
        list.findIndex((candidate) => fileKey(candidate) === fileKey(file)) ===
        index,
    );
    const error = attachmentSelectionError(combined);
    if (error) {
      onError(error);
      return;
    }
    onChange(combined);
  };

  return (
    <div className="certification-attachment-field">
      <span>{label}</span>
      <label
        className={`certification-attachment-dropzone${files.length ? " has-files" : ""}`}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          addFiles(Array.from(event.dataTransfer.files));
        }}
      >
        <input
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          onChange={(event) => {
            addFiles(Array.from(event.currentTarget.files || []));
            event.currentTarget.value = "";
          }}
        />
        <Upload />
        <span>
          <strong>
            {files.length
              ? `${files.length} arquivo(s) selecionado(s)`
              : "Clique ou arraste arquivos para esta área"}
          </strong>
          <small>
            Compactados, imagens, planilhas, vídeos, áudios ou PDF · até 30 MB
            na seção
          </small>
        </span>
      </label>
      {files.length > 0 && (
        <ul className="certification-pending-attachments">
          {files.map((file) => (
            <li key={fileKey(file)}>
              <Paperclip />
              <span>
                <strong>{file.name}</strong>
                <small>{formatSize(file.size)}</small>
              </span>
              <button
                type="button"
                aria-label={`Remover ${file.name}`}
                onClick={() =>
                  onChange(files.filter((candidate) => candidate !== file))
                }
              >
                <X />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
